"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import {
	chipLabelToMode,
	createJob,
	modeToClipCount,
	uploadAudioToJob,
} from "@/lib/analyze";
import { helperDownloadAudio, helperPing } from "@/lib/helper-client";
import { ChangelogNotification } from "@/components/editor/floating/ChangelogNotification";
import { HelpModal } from "@/components/editor/floating/HelpModal";
import { OnboardingModal } from "@/components/editor/floating/OnboardingModal";
import { PreAnalysisModal } from "@/components/editor/floating/PreAnalysisModal";
import { TemplatePreSelectModal } from "@/components/editor/floating/TemplatePreSelectModal";
import { BatchPanel, type BatchPanelItem } from "./BatchPanel";
import { Hero } from "./Hero";
import { MainCreateCard } from "./MainCreateCard";
import { ToolsCardGrid } from "./ToolsCardGrid";
import { ProjectsList } from "./ProjectsList";
import { HelperStatusDot, type HelperStatus } from "./HelperStatusDot";
import { HelperFallbackBanner } from "./HelperFallbackBanner";
import styles from "./WelcomePage.module.css";

/**
 * WelcomePage — Phase 25-AB Task 10 (panel W).
 *
 * Composite Welcome route. Two-column layout: sidebar (200px) + main. Hero
 * header on top, MainCreateCard (5 modes: URL/file/script/multicam/batch) in
 * the upper main, then ToolsCardGrid (9 tools) and ProjectsList. Floating
 * mounts: Changelog (bottom-left), Onboarding (first-visit), PreAnalysisModal
 * and TemplatePreSelectModal (both triggered from Analyze flow).
 *
 * Mockup source: docs/mockups/welcome.html (1139 lines, primary source).
 *
 * Task 18 components added: PreAnalysisModal (#7), TemplatePreSelectModal
 * (#10), HelperStatusDot (#16), HelperFallbackBanner (#15).
 */

const ONBOARDING_KEY = "mml.onboarding.seen";

export function WelcomePage() {
	const router = useRouter();
	const [showOnboarding, setShowOnboarding] = useState(false);
	const [preAnalysisOpen, setPreAnalysisOpen] = useState(false);
	const [templateOpen, setTemplateOpen] = useState(false);
	// Phase 25 — real preview metadata fetched after URL entry (was a hardcoded
	// "노을 드라이브" placeholder for every URL; user wouldn't see their actual
	// video meta, looked completely broken). YouTube/TikTok/Vimeo oembed is
	// public + CORS-friendly; local files use the File object directly.
	const [previewMeta, setPreviewMeta] = useState<
		import("@/components/editor/floating/PreAnalysisModal").PreAnalysisVideoMeta | undefined
	>(undefined);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [helperStatus, setHelperStatus] = useState<HelperStatus>("checking");

	// Analyze flow state — the URL/input + the selected mode chip are captured
	// when the user clicks Analyze, then used to create the job once they pick
	// a template (or "템플릿 없이").
	const [analyzeInput, setAnalyzeInput] = useState("");
	const [analyzeFile, setAnalyzeFile] = useState<File | null>(null);
	const [analyzeMode, setAnalyzeMode] = useState("AI + 카테고리 자동");
	const [creating, setCreating] = useState(false);
	const [analyzeError, setAnalyzeError] = useState<string | null>(null);

	// Increment 2 — Welcome handler wiring. helpOpen drives the HelpModal;
	// infoToast surfaces honest feedback for controls whose backend is a later
	// increment (so no Welcome button is a silent noop).
	const [helpOpen, setHelpOpen] = useState(false);
	const [infoToast, setInfoToast] = useState<string | null>(null);
	const createCardRef = useRef<HTMLDivElement>(null);

	/**
	 * Tool grid (panel 12) — route the tools that have a real destination,
	 * scroll to the analyze card for the analyze-backed tools, and give honest
	 * "not built yet" feedback for the rest. Audit: before this the 9 tool
	 * cards were silent noops (ToolsCardGrid rendered without onToolClick).
	 */
	function handleToolClick(toolId: string) {
		switch (toolId) {
			case "ai-magic-wand":
				router.push("/dev/magic-wand");
				return;
			case "tts-narration":
				router.push("/dev/narration");
				return;
			case "long-to-shorts":
			case "batch":
				createCardRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
				setInfoToast(
					toolId === "batch"
						? "분석 시작 카드에서 batch 탭을 선택하세요."
						: "분석 시작 카드에 URL을 입력하세요.",
				);
				return;
			default:
				// ai-video / ai-image / translator / hook-analysis / quality-fix
				setInfoToast(
					"이 도구는 아직 구현 전입니다 — 다음 increment 예정 (감사 진실표 참고).",
				);
		}
	}

	/**
	 * Script mode — POST the written script to the live Worker endpoint
	 * `/ai/script-to-scenes` (Worker → FastAPI core → Claude). 401 -> /login.
	 * Before this it was an honest placeholder toast (Increment 2).
	 */
	async function handleScriptGenerate(script: string, engine: string) {
		const trimmed = script.trim();
		if (!trimmed) {
			setInfoToast("스크립트를 입력하세요.");
			return;
		}
		setCreating(true);
		try {
			const r = await fetch("/api/youtube-shorts/ai/script-to-scenes", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ script: trimmed, engine }),
			});
			if (r.status === 401) {
				router.push("/login");
				return;
			}
			const json = (await r.json().catch(() => ({}))) as {
				error?: string;
				balance?: number;
				required?: number;
			};
			if (!r.ok) {
				if (json.error === "insufficient_credits") {
					setInfoToast(
						`코인이 부족합니다 (보유 ${json.balance ?? 0} · 필요 ${json.required ?? "?"}).`,
					);
					return;
				}
				setInfoToast(
					`스크립트 분석 실패: ${json.error ?? `오류 ${r.status}`}`,
				);
				return;
			}
			setInfoToast("스크립트 → 씬 분해 완료 — 백엔드에서 결과를 받았습니다.");
		} catch (e) {
			setInfoToast(`스크립트 분석 오류: ${String(e)}`);
		} finally {
			setCreating(false);
		}
	}

	/**
	 * Multicam (Increment 15) — create one analysis job per picked camera
	 * File, uploading each file's bytes. Same backend path as file-upload.
	 * 401 -> /login. Reports how many cameras were queued.
	 */
	async function handleMulticamAnalyze(files: File[]) {
		if (files.length === 0) {
			setInfoToast("카메라 영상 파일을 1개 이상 선택하세요.");
			return;
		}
		setCreating(true);
		setAnalyzeError(null);
		const clipCount = modeToClipCount(chipLabelToMode(analyzeMode));
		let done = 0;
		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			const job = await createJob({
				source_url: file.name,
				source_kind: "local",
				source_filename: file.name,
				source_title: `CAM ${i + 1} · ${file.name}`,
				target_clip_count: clipCount,
			});
			if (!job.ok || !job.jobId) {
				setCreating(false);
				if (job.error === "unauthenticated" || job.status === 401) {
					router.push("/login");
					return;
				}
				setAnalyzeError(
					`멀티캠 ${i + 1}번 카메라 작업 실패: ${job.error ?? "알 수 없는 오류"} (성공 ${done}개).`,
				);
				return;
			}
			const bytes = await file.arrayBuffer();
			await uploadAudioToJob(job.jobId, bytes, file.name, {
				source_title: `CAM ${i + 1}`,
				target_clip_count: clipCount,
			});
			done += 1;
		}
		setCreating(false);
		setInfoToast(`멀티캠 ${done}개 카메라 분석 작업이 큐에 생성되었습니다.`);
	}

	function handleBatchStart() {
		// BatchMode items carry no URL field — its queue is a UI sample only.
		setInfoToast(
			"이 batch 탭은 UI 큐입니다 — 아래 멀티 batch 패널의 '전체 시작'을 쓰세요.",
		);
	}

	/**
	 * Real batch processing — BatchPanel items DO carry URLs, so loop
	 * createJob over them. Hits the same backend as the single-URL analyze
	 * flow; 401 routes to /login. Before this, "전체 시작" was a silent noop.
	 */
	async function handleBatchPanelStartAll(items: BatchPanelItem[]) {
		const urls = items
			.map((it) => it.url.trim())
			.filter((u) => u.length > 0);
		if (urls.length === 0) {
			setInfoToast("큐에 처리할 URL이 없습니다.");
			return;
		}
		setCreating(true);
		setAnalyzeError(null);
		let created = 0;
		for (const url of urls) {
			const res = await createJob({
				source_url: url,
				source_kind: "youtube",
				target_clip_count: modeToClipCount(chipLabelToMode(analyzeMode)),
			});
			if (!res.ok) {
				setCreating(false);
				if (res.error === "unauthenticated" || res.status === 401) {
					router.push("/login");
					return;
				}
				setAnalyzeError(
					`batch ${created + 1}번째 작업 실패: ${res.error ?? "알 수 없는 오류"} (성공 ${created}개).`,
				);
				return;
			}
			created += 1;
		}
		setCreating(false);
		setInfoToast(`batch ${created}개 분석 작업이 큐에 생성되었습니다.`);
	}

	/**
	 * Sidebar nav (panel 10) — route the items with a real destination, give
	 * honest feedback for the not-yet-built ones. Before this every sidebar
	 * NavItem + the profile / coin cards were silent (no onClick).
	 */
	function handleNav(dest: string) {
		switch (dest) {
			case "home":
				createCardRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
				return;
			case "magic-wand":
				router.push("/dev/magic-wand");
				return;
			case "tts-narration":
				router.push("/dev/narration");
				return;
			case "coin-charge":
				router.push("/login");
				return;
			default:
				setInfoToast(
					"이 메뉴는 아직 구현 전입니다 — 다음 increment 예정 (감사 진실표 참고).",
				);
		}
	}

	useEffect(() => {
		if (typeof window === "undefined") return;
		try {
			const seen = window.localStorage.getItem(ONBOARDING_KEY);
			if (!seen) setShowOnboarding(true);
		} catch {
			/* private mode — skip */
		}
	}, []);

	function closeOnboarding() {
		try {
			window.localStorage.setItem(ONBOARDING_KEY, "1");
		} catch {
			/* ignore */
		}
		setShowOnboarding(false);
	}

	/**
	 * Pull lightweight preview metadata for the URL the user entered, via the
	 * source's public oembed endpoint (YouTube / Vimeo / TikTok — all
	 * CORS-friendly + no API key). Returns `undefined` so the PreAnalysisModal
	 * falls back to its own honest placeholder when we can't reach oembed.
	 */
	async function fetchPreviewMeta(url: string) {
		try {
			let oembedUrl: string | null = null;
			if (/youtube\.com|youtu\.be/.test(url)) {
				oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
			} else if (/vimeo\.com/.test(url)) {
				oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`;
			} else if (/tiktok\.com/.test(url)) {
				oembedUrl = `https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`;
			}
			if (!oembedUrl) return undefined;
			const r = await fetch(oembedUrl, { cache: "no-store" });
			if (!r.ok) return undefined;
			const j = (await r.json()) as {
				title?: string;
				author_name?: string;
				thumbnail_url?: string;
			};
			return {
				thumbUrl: j.thumbnail_url,
				title: j.title ?? "(제목 가져오지 못함)",
				channel: j.author_name ?? "(채널 가져오지 못함)",
				language: "분석 후 확인",
				license: "분석 후 확인",
				segmentLabel: "00:00 ~ 끝 (전체)",
			};
		} catch {
			return undefined;
		}
	}

	function handleAnalyze(input: string, mode: string) {
		const trimmed = input.trim();
		if (!trimmed) {
			setAnalyzeError("URL을 입력하세요.");
			return;
		}
		setAnalyzeError(null);
		setAnalyzeFile(null);
		setAnalyzeInput(trimmed);
		setAnalyzeMode(mode);
		setPreviewMeta(undefined);
		setPreviewLoading(true);
		setPreAnalysisOpen(true);
		// Fire-and-forget — modal opens immediately with a loading state, then
		// fills in once oembed responds (typical 200~500 ms).
		fetchPreviewMeta(trimmed)
			.then((m) => setPreviewMeta(m))
			.finally(() => setPreviewLoading(false));
	}

	/**
	 * File-upload mode (Increment 14) — a real local video File enters the
	 * same PreAnalysis → Template → createJob flow. handleTemplateSelect
	 * branches on `analyzeFile`: it creates a `local` job then uploads the
	 * file's bytes via uploadAudioToJob (was: only the filename string was
	 * passed and wrongly treated as a URL).
	 */
	function handleFileAnalyze(file: File, mode: string) {
		setAnalyzeError(null);
		setAnalyzeFile(file);
		setAnalyzeInput(file.name);
		setAnalyzeMode(mode);
		// Local file: no oembed, but give the user real-meta visibility from
		// the File object so the modal isn't a lying placeholder.
		setPreviewMeta({
			title: file.name,
			channel: `로컬 파일 · ${(file.size / 1024 / 1024).toFixed(1)} MB`,
			language: "분석 후 확인",
			license: "로컬 파일 — 사용자 소유",
			segmentLabel: "00:00 ~ 끝 (전체)",
		});
		setPreviewLoading(false);
		setPreAnalysisOpen(true);
	}

	function handlePreAnalysisProceed() {
		setPreAnalysisOpen(false);
		setTemplateOpen(true);
	}

	/**
	 * Final step of the analyze flow: create the job and route to the live
	 * analysis dashboard. Called from TemplatePreSelectModal.
	 *
	 * Two paths:
	 *  - Helper running  → create a `local` job (Worker skips its cloud
	 *    download), download audio via the Helper's bundled yt-dlp
	 *    (residential IP — not bot-blocked), upload it to the job.
	 *  - Helper offline  → create a `youtube` job; the Worker triggers
	 *    server-side yt-dlp (works for non-YouTube sources; YouTube may be
	 *    bot-blocked from the datacenter IP).
	 *
	 * `_templateId` is captured for a future templates feature.
	 */
	async function handleTemplateSelect(_templateId: string | null) {
		if (creating) return;
		setCreating(true);
		setAnalyzeError(null);
		const clipCount = modeToClipCount(chipLabelToMode(analyzeMode));

		/** Map a createJob failure to UX; returns true if it was handled. */
		function handleCreateFailure(result: {
			error?: string;
			status?: number;
			balance?: number;
			required?: number;
		}): boolean {
			if (result.error === "unauthenticated" || result.status === 401) {
				setCreating(false);
				router.push("/login");
				return true;
			}
			if (result.error === "insufficient_credits") {
				setCreating(false);
				setAnalyzeError(
					`코인이 부족합니다 (보유 ${result.balance ?? 0} · 필요 ${result.required ?? "?"}).`,
				);
				return true;
			}
			return false;
		}

		// Local file path (Increment 14) — a real picked File. Create a `local`
		// job, then upload the file's bytes directly to /jobs/:id/analyze.
		if (analyzeFile) {
			const file = analyzeFile;
			const job = await createJob({
				source_url: file.name,
				source_kind: "local",
				source_filename: file.name,
				target_clip_count: clipCount,
			});
			if (!job.ok || !job.jobId) {
				if (!handleCreateFailure(job)) {
					setCreating(false);
					setAnalyzeError(
						`분석 시작 실패: ${job.error ?? "알 수 없는 오류"}`,
					);
				}
				return;
			}
			const bytes = await file.arrayBuffer();
			const uploaded = await uploadAudioToJob(job.jobId, bytes, file.name, {
				source_title: file.name,
				target_clip_count: clipCount,
			});
			setCreating(false);
			if (!uploaded) {
				setAnalyzeError("파일 업로드 실패 — 다시 시도하세요.");
				return;
			}
			router.push(`/editor/${job.jobId}/analysis`);
			return;
		}

		const helperUp = await helperPing();

		if (helperUp) {
			// Helper path — residential-IP download.
			const job = await createJob({
				source_url: analyzeInput,
				source_kind: "local",
				source_filename: analyzeInput,
				target_clip_count: clipCount,
			});
			if (!job.ok || !job.jobId) {
				if (!handleCreateFailure(job)) {
					setCreating(false);
					setAnalyzeError(`분석 시작 실패: ${job.error ?? "알 수 없는 오류"}`);
				}
				return;
			}
			const audio = await helperDownloadAudio(analyzeInput);
			if (!audio) {
				setCreating(false);
				setAnalyzeError(
					"Helper 다운로드 실패 — URL을 확인하거나 잠시 후 다시 시도하세요.",
				);
				return;
			}
			const uploaded = await uploadAudioToJob(
				job.jobId,
				audio.bytes,
				audio.filename,
				{ target_clip_count: clipCount },
			);
			setCreating(false);
			if (!uploaded) {
				setAnalyzeError("오디오 업로드 실패 — 다시 시도하세요.");
				return;
			}
			router.push(`/editor/${job.jobId}/analysis`);
			return;
		}

		// Cloud path — Worker triggers server-side yt-dlp.
		const result = await createJob({
			source_url: analyzeInput,
			source_kind: "youtube",
			target_clip_count: clipCount,
		});
		setCreating(false);
		if (result.ok && result.jobId) {
			router.push(`/editor/${result.jobId}/analysis`);
			return;
		}
		if (handleCreateFailure(result)) return;
		setAnalyzeError(`분석 시작 실패: ${result.error ?? "알 수 없는 오류"}`);
	}

	const bannerVisible =
		helperStatus === "offline" || helperStatus === "fallback";

	return (
		<main
			className={styles.welcome}
			data-testid="welcome-page"
			{...devLabelPanel("W")}
		>
			<Hero
				helperStatusNode={
					<HelperStatusDot
						initial="checking"
						onStatusChange={setHelperStatus}
					/>
				}
				onHelpClick={() => setHelpOpen(true)}
			/>
			<HelperFallbackBanner visible={bannerVisible} />
			<div className={styles.body}>
				<aside
					className={styles.side}
					data-testid="welcome-sidebar"
					{...devLabelPanel("10 · Sidebar")}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							padding: 6,
							borderRadius: 8,
							background: "#2a2a2a",
						}}
						{...devLabel("10-A")}
					>
						<div
							style={{
								width: 32,
								height: 32,
								borderRadius: "50%",
								background:
									"linear-gradient(135deg,#8a6a4e,#3a2a1e)",
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: 12,
								fontWeight: 700,
							}}
						>
							M
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div
								style={{
									fontSize: 11,
									fontWeight: 600,
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								makemonlab
							</div>
							<div
								style={{
									fontSize: 10,
									opacity: 0.55,
									fontFamily: "'Geist Mono',monospace",
								}}
							>
								0066191303
							</div>
						</div>
					</div>
					<div
						role="button"
						tabIndex={0}
						onClick={() => handleNav("coin-charge")}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ")
								handleNav("coin-charge");
						}}
						style={{
							padding: "10px 12px",
							borderRadius: 8,
							background:
								"linear-gradient(135deg,#d4af37 0%,#a07f23 100%)",
							color: "#0a0a0a",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							fontWeight: 700,
							fontSize: 12,
							cursor: "pointer",
						}}
						{...devLabel("10-B")}
					>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 5,
							}}
						>
							<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
								<polygon points="12 2 15 9 22 9.3 17 14 18.5 21 12 17.3 5.5 21 7 14 2 9.3 9 9 12 2" />
							</svg>
							코인 충전
						</span>
						<span
							style={{
								fontFamily: "'Geist Mono',monospace",
								fontSize: 13,
							}}
						>
							247
						</span>
					</div>
					<SidebarNav onNav={handleNav} />
				</aside>
				<section
					className={styles.main}
					data-testid="welcome-main"
					{...devLabelPanel("11 · Main")}
				>
					<div
						ref={createCardRef}
						className={styles.heroRow}
						style={{ marginBottom: 18 }}
					>
						<MainCreateCard
							onAnalyze={handleAnalyze}
							onFileAnalyze={handleFileAnalyze}
							onBatchStart={handleBatchStart}
							onScriptGenerate={handleScriptGenerate}
							onMulticamAnalyze={handleMulticamAnalyze}
						/>
					</div>
					<ToolsCardGrid onToolClick={handleToolClick} />
					<div style={{ marginBottom: 24 }}>
						<BatchPanel onStartAll={handleBatchPanelStartAll} />
					</div>
					<ProjectsList />
				</section>
			</div>

			{/* Floating: Changelog (auto-dismiss) */}
			<ChangelogNotification version="0.4.0" />

			{/* Floating: Help (header help button) */}
			<HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

			{/* Floating: Onboarding (first-visit only) */}
			<OnboardingModal open={showOnboarding} onClose={closeOnboarding} />

			{/* Floating: PreAnalysisModal (after Analyze click) */}
			<PreAnalysisModal
				open={preAnalysisOpen}
				onClose={() => setPreAnalysisOpen(false)}
				onProceed={handlePreAnalysisProceed}
				meta={previewMeta}
				loading={previewLoading}
			/>

			{/* Floating: TemplatePreSelectModal (after PreAnalysisModal proceed) */}
			<TemplatePreSelectModal
				open={templateOpen}
				onClose={() => setTemplateOpen(false)}
				onSelect={handleTemplateSelect}
			/>

			{/* Job creation in-flight overlay */}
			{creating && (
				<div
					data-testid="analyze-creating"
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(10,10,10,0.78)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 200,
						fontSize: 14,
						color: "#d4af37",
						fontWeight: 700,
					}}
				>
					분석 작업 생성 중…
				</div>
			)}

			{/* Analyze error toast */}
			{analyzeError && (
				<div
					data-testid="analyze-error"
					role="alert"
					onClick={() => setAnalyzeError(null)}
					style={{
						position: "fixed",
						bottom: 20,
						left: "50%",
						transform: "translateX(-50%)",
						background: "#161616",
						border: "1px solid #ef4444",
						color: "#ef4444",
						padding: "10px 18px",
						borderRadius: 8,
						fontSize: 12,
						fontWeight: 600,
						zIndex: 201,
						cursor: "pointer",
						maxWidth: "90vw",
					}}
				>
					{analyzeError}
				</div>
			)}

			{/* Info toast — honest feedback for not-yet-built controls */}
			{infoToast && (
				<div
					data-testid="info-toast"
					role="status"
					onClick={() => setInfoToast(null)}
					style={{
						position: "fixed",
						bottom: 20,
						left: "50%",
						transform: "translateX(-50%)",
						background: "#161616",
						border: "1px solid #d4af37",
						color: "#d4af37",
						padding: "10px 18px",
						borderRadius: 8,
						fontSize: 12,
						fontWeight: 600,
						zIndex: 201,
						cursor: "pointer",
						maxWidth: "90vw",
					}}
				>
					{infoToast}
				</div>
			)}
		</main>
	);
}

function SidebarNav({ onNav }: { onNav: (dest: string) => void }) {
	return (
		<>
			<div>
				<div
					style={{
						fontSize: 11,
						fontWeight: 600,
						opacity: 0.55,
						marginBottom: 6,
						padding: "0 4px",
					}}
				>
					동영상 편집
				</div>
				<NavItem active label="홈" onClick={() => onNav("home")} />
				<NavItem label="템플릿" onClick={() => onNav("templates")} />
				<NavItem
					label="공간"
					trailingArrow
					onClick={() => onNav("space")}
				/>
			</div>
			<div>
				<NavItem
					label="디자인 스튜디오"
					newBadge
					onClick={() => onNav("design-studio")}
				/>
				<NavItem label="AI로 만들기" onClick={() => onNav("ai-create")} />
				<NavItem label="마케팅 도구" onClick={() => onNav("marketing")} />
			</div>
			<div>
				<div
					style={{
						fontSize: 11,
						fontWeight: 600,
						opacity: 0.55,
						marginBottom: 6,
						padding: "0 4px",
						color: "#d4af37",
					}}
				>
					우리 도구{" "}
					<span
						style={{
							display: "inline-block",
							padding: "1px 4px",
							fontSize: 8,
							fontWeight: 700,
							letterSpacing: "0.05em",
							background: "#d4af37",
							color: "#0a0a0a",
							borderRadius: 2,
							verticalAlign: "middle",
							lineHeight: 1.4,
						}}
					>
						MML+
					</span>
				</div>
				<NavItem
					label="YouTube → Shorts"
					gold
					onClick={() => onNav("home")}
				/>
				<NavItem
					label="AI Magic Wand"
					gold
					onClick={() => onNav("magic-wand")}
				/>
				<NavItem
					label="TTS 나레이션"
					gold
					onClick={() => onNav("tts-narration")}
				/>
				<NavItem
					label="분석 결과 보기"
					gold
					onClick={() => onNav("analysis-results")}
				/>
				<NavItem
					label="다중 플랫폼 업로드"
					gold
					onClick={() => onNav("multi-upload")}
				/>
			</div>
		</>
	);
}

function NavItem({
	label,
	active,
	gold,
	newBadge,
	trailingArrow,
	onClick,
}: {
	label: string;
	active?: boolean;
	gold?: boolean;
	newBadge?: boolean;
	trailingArrow?: boolean;
	onClick?: () => void;
}) {
	return (
		<div
			role="button"
			tabIndex={0}
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") onClick?.();
			}}
			style={{
				display: "flex",
				alignItems: "center",
				gap: 8,
				padding: "8px 10px",
				borderRadius: 6,
				fontSize: 12,
				cursor: "pointer",
				background: active ? "rgba(78,179,193,0.1)" : "transparent",
				color: active ? "#4eb3c1" : gold ? "#d4af37" : "#dcdcdc",
				transition: "background 0.15s",
			}}
		>
			<span style={{ fontSize: 14, width: 18, textAlign: "center" }}>•</span>
			<span>{label}</span>
			{newBadge && (
				<span
					style={{
						marginLeft: "auto",
						padding: "1px 5px",
						fontSize: 9,
						fontWeight: 700,
						background: "#d4af37",
						color: "#0a0a0a",
						borderRadius: 3,
					}}
				>
					New
				</span>
			)}
			{trailingArrow && (
				<span style={{ marginLeft: "auto", fontSize: 9, opacity: 0.5 }}>›</span>
			)}
		</div>
	);
}
